<template>
  <div
    :class="cardClass"
    :style="cardStyle"
    @click="openAppointment(data)"
    @contextmenu.prevent="showContextMenu"
    @mouseenter="calculatePopoverPosition"
    @mouseleave="hidePopover"
  >
    <div class="week-appointment-card-main">
      <div class="week-appointment-card-time">
        <strong>{{ startTimeLabel }}</strong>
        <span class="week-appointment-card-time-separator">-</span>
        <span>{{ endTimeLabel }}</span>
      </div>
      <div
        v-if="isTaken && data.patient && data.patient.name"
        class="week-appointment-card-patient"
      >
        {{ data.patient.name }}
      </div>
    </div>
    <div
      v-if="isTaken"
      ref="popoverContainer"
      class="popover-container"
      :style="popoverStyle"
    >
      <div class="card">
        <div class="card-body">
          <div class="popover-content-item">
            <strong v-if="isSlot">Encaixe:</strong>
            <strong v-else>Horário:</strong>
            <span>{{ startTimeLabel }} - {{ endTimeLabel }}</span>
          </div>
          <div
            v-if="data.patient && data.patient.name"
            class="popover-content-item"
          >
            <strong>Paciente:</strong>
            <span>{{ data.patient.name }}</span>
          </div>
          <div
            v-if="patientPhone"
            class="popover-content-item"
          >
            <strong>Telefone:</strong>
            <span>{{ patientPhone | phone }}</span>
          </div>
          <div
            v-if="firstProcedure"
            class="popover-content-item"
          >
            <strong>Procedimento:</strong>
            <span>{{ firstProcedure }}</span>
          </div>
          <div
            v-if="professionalName"
            class="popover-content-item"
          >
            <strong>Profissional:</strong>
            <span>{{ professionalName }}</span>
          </div>
          <div
            v-if="insuranceName"
            class="popover-content-item"
          >
            <strong>Convênio:</strong>
            <span>{{ insuranceName }}</span>
          </div>
          <div
            v-if="statusName"
            class="popover-content-item"
          >
            <strong>Status:</strong>
            <span>{{ statusName }}</span>
          </div>
          <div
            v-if="data.notes"
            class="popover-content-item"
          >
            <strong>Observações:</strong>
            <span>{{ data.notes }}</span>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="contextMenuVisible && !waiting"
      class="context-menu"
      :style="contextMenuStyle"
      @click.stop
      v-click-outside="hideContextMenu"
    >
      <ul class="menu">
        <li
          v-for="(item, index) in validMenuOptions"
          :key="index"
          class="menu-item"
          @click.stop="handleMenuAction(item, $event)"
        >
          <div class="menu-item-content">
            <fa-icon v-if="item.icon" class="menu-item-icon" :icon="item.icon" />
            <span>{{ item.name }}</span>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { getColor } from '@/data/appointment-statuses';
import TableRow from '../TableRow.vue';

export default {
  name: 'WeekAppointmentCard',
  extends: TableRow,
  data() {
    return {
      contextMenuVisible: false,
      contextMenuStyle: {},
      popoverStyle: {},
      popoverVisible: false,
    };
  },
  computed: {
    cardStyle() {
      const statusColor = getColor(this.data?.status);
      const style = {};

      if (statusColor) {
        if (this.data?.status === 'available' || !this.isTaken) {
          style.backgroundColor = '#FFFFFF';
        } else {
          style.backgroundColor = statusColor;
          style.color = this.getContrastColor(statusColor);
        }
      } else if (!this.isTaken) {
        style.backgroundColor = '#FFFFFF';
      }

      return style;
    },
    cardClass() {
      const classes = Array.isArray(this.classes) ? this.classes.slice() : [];
      classes.push('week-appointment-card');
      classes.push(this.stateModifier);
      if (this.isTaken) {
        classes.push('popover');
        classes.push('popover-right');
      }
      return classes;
    },
    startTimeLabel() {
      if (this.data && this.data.startDate) {
        const start = moment(this.data.startDate);
        if (start.isValid()) {
          return start.format('HH:mm');
        }
      }
      return '';
    },
    endTimeLabel() {
      if (this.data && this.data.endDate) {
        const end = moment(this.data.endDate);
        if (end.isValid()) {
          return end.format('HH:mm');
        }
      }
      if (this.data && this.data.startDate) {
        const start = moment(this.data.startDate);
        if (start.isValid()) {
          return start.clone().add(30, 'minutes').format('HH:mm');
        }
      }
      return '';
    },
    isTaken() {
      return !!(this.data && this.data.patient && this.data.patient.name);
    },
    stateModifier() {
      if (this.isBlocked) {
        return 'week-appointment-card--blocked';
      }
      if (this.isCanceled) {
        return 'week-appointment-card--canceled';
      }
      if (this.isTaken) {
        return 'week-appointment-card--taken';
      }
      return 'week-appointment-card--free';
    },
    validMenuOptions() {
      return this.menuAppointmentOptions.filter(
        item => item && item !== '-' && typeof item === 'object' && item.name,
      );
    },
    isSlot() {
      return this.data.isSlot && !!this.data.patient;
    },
    patientPhone() {
      if (this.data.patient) {
        return this.data.patient.cellphone || this.data.patient.phone || null;
      }
      return null;
    },
    firstProcedure() {
      if (this.data.expenses
        && Array.isArray(this.data.expenses)
        && this.data.expenses.length > 0) {
        const firstExpense = this.data.expenses[0];
        if (firstExpense && firstExpense.expense && firstExpense.expense.name) {
          return firstExpense.expense.name;
        }
      }
      return null;
    },
  },
  methods: {
    getContrastColor(hexColor) {
      if (!hexColor) return '#000000';

      const hex = hexColor.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);

      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

      return luminance > 0.5 ? '#000000' : '#FFFFFF';
    },
    showContextMenu(event) {
      if (this.validMenuOptions.length === 0 || this.waiting) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      this.contextMenuVisible = true;
      this.$nextTick(() => {
        const menuWidth = 200;
        const menuHeight = 300;
        const x = Math.min(event.clientX, window.innerWidth - menuWidth);
        const y = Math.min(event.clientY, window.innerHeight - menuHeight);
        this.contextMenuStyle = {
          position: 'fixed',
          left: `${x}px`,
          top: `${y}px`,
          zIndex: 10000,
        };
      });
    },
    hideContextMenu(event) {
      if (event && event.target && this.$el && this.$el.contains(event.target)) {
        return;
      }
      this.contextMenuVisible = false;
    },
    handleMenuAction(item, event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      this.hideContextMenu();
      this.menuAction(item, this.data);
    },
    calculatePopoverPosition() {
      if (!this.isTaken || !this.$el || !this.$refs.popoverContainer) {
        return;
      }

      this.popoverStyle = {
        display: 'block',
        visibility: 'hidden',
        left: '100%',
        top: '0',
        marginLeft: '8px',
      };

      this.$nextTick(() => {
        const cardRect = this.$el.getBoundingClientRect();
        const popoverElement = this.$refs.popoverContainer;
        const popoverRect = popoverElement.getBoundingClientRect();

        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const gap = 8;

        let left = '100%';
        let marginLeft = gap;
        let marginRight = 'auto';
        let right = 'auto';
        let transform = '';

        const spaceRight = viewportWidth - cardRect.right;
        const spaceLeft = cardRect.left;
        const popoverWidth = popoverRect.width || 300;
        const neededSpace = popoverWidth + gap;

        if (spaceRight < neededSpace && spaceLeft >= neededSpace) {
          left = 'auto';
          right = '100%';
          marginLeft = 'auto';
          marginRight = gap;
        } else if (spaceRight < neededSpace && spaceLeft < neededSpace) {
          if (spaceRight > spaceLeft) {
            left = '50%';
            transform = 'translateX(-50%)';
            marginLeft = 'auto';
            marginRight = 'auto';
          } else {
            right = '50%';
            transform = 'translateX(50%)';
            marginLeft = 'auto';
            marginRight = 'auto';
          }
        }

        let top = '0';
        let marginTop = '0';
        let marginBottom = 'auto';
        let bottom = 'auto';

        const spaceBelow = viewportHeight - cardRect.bottom;
        const spaceAbove = cardRect.top;
        const popoverHeight = popoverRect.height || 200;
        const neededVerticalSpace = popoverHeight + gap;

        if (spaceBelow < neededVerticalSpace && spaceAbove >= neededVerticalSpace) {
          top = 'auto';
          bottom = '0';
          marginTop = 'auto';
          marginBottom = gap;
        } else if (spaceBelow < neededVerticalSpace && spaceAbove < neededVerticalSpace) {
          if (spaceBelow > spaceAbove) {
            top = '50%';
            transform = transform
              ? `${transform} translateY(-50%)`
              : 'translateY(-50%)';
          } else {
            bottom = '50%';
            transform = transform
              ? `${transform} translateY(50%)`
              : 'translateY(50%)';
          }
        }

        this.popoverStyle = {
          left: left !== 'auto' ? left : undefined,
          right: right !== 'auto' ? right : undefined,
          top: top !== 'auto' ? top : undefined,
          bottom: bottom !== 'auto' ? bottom : undefined,
          marginLeft: marginLeft !== 'auto' ? `${marginLeft}px` : 'auto',
          marginRight: marginRight !== 'auto' ? `${marginRight}px` : 'auto',
          marginTop: marginTop !== 'auto' ? `${marginTop}px` : 'auto',
          marginBottom: marginBottom !== 'auto' ? `${marginBottom}px` : 'auto',
          transform: transform || undefined,
          display: 'block',
          visibility: 'visible',
        };

        this.popoverVisible = true;
      });
    },
    hidePopover() {
      this.popoverVisible = false;
      this.popoverStyle = {
        display: 'none',
      };
    },
  },
  mounted() {
    document.addEventListener('click', this.hideContextMenu);
    document.addEventListener('contextmenu', this.hideContextMenu);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.hideContextMenu);
    document.removeEventListener('contextmenu', this.hideContextMenu);
  },
};
</script>

<style lang="scss">
@import '../../../../assets/scss/variables';

.week-appointment-card {
  border: $border-width solid rgba($gray-color, .3);
  border-radius: 0;
  display: flex;
  flex-direction: column;
  padding: $layout-spacing-sm;
  width: 100%;
  height: 100%;
  cursor: pointer;
  position: relative;
  box-sizing: border-box;
  margin-bottom: 2px;
  opacity: 1;

  &.popover {
    overflow: visible;
  }

  &:not(.popover) {
    overflow: hidden;
  }

  &:hover {
    opacity: 1;
  }
}

.week-appointment-card.week-appointment-card--free {
  color: $dark-color;
  background-color: #FFFFFF !important;
  border-color: transparent;
  border-width: 2px;
  pointer-events: auto;

  .week-appointment-card-time {
    opacity: 0;
  }

  &:hover {
    background-color: #FFFFFF !important;
    border-color: $primary-color;

    .week-appointment-card-time {
      opacity: 1;
    }
  }
}

.week-appointment-card.week-appointment-card--blocked {
  color: $gray-color-dark;
  background: repeating-linear-gradient(
    45deg,
    lighten($gray-color-light, 4%) 0,
    lighten($gray-color-light, 4%) 8px,
    lighten($gray-color-light, 8%) 8px,
    lighten($gray-color-light, 8%) 16px
  );
  border-color: rgba($gray-color, .5);
}

.week-appointment-card.week-appointment-card--canceled {
  color: $dark-color;
}

.context-menu {
  background: transparent;
  border: none;
  border-radius: 0;
  box-shadow: none;
  min-width: 14rem;
  padding: 0;
}

.context-menu .menu {
  margin: 0;
  padding: $layout-spacing-sm;
  list-style: none;
  background-color: $light-color;
  border-radius: $border-radius;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  color: $dark-color;
}

.context-menu .menu-item {
  padding: $layout-spacing-sm;
  display: flex;
  align-items: center;
  cursor: pointer;
  border-radius: $border-radius;
  color: $dark-color;

  &:hover {
    background-color: rgba($primary-color, .12);
    color: $dark-color;
  }
}

.context-menu .menu-item-content {
  display: flex;
  align-items: center;
  gap: calc(#{$layout-spacing-sm} / 2);
  width: 100%;
  color: $dark-color;
}

.context-menu .menu-item-icon {
  font-size: $font-size-sm;
  margin-right: 4px;
}

.week-appointment-card-main {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  height: 100%;
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

.week-appointment-card-time {
  display: flex;
  align-items: baseline;
  gap: calc(#{$layout-spacing-sm} / 4);
  font-size: $font-size-xs;
  min-width: 0;
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.week-appointment-card-time strong {
  font-size: 12px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.week-appointment-card-time-separator {
  font-weight: 600;
  font-size: $font-size-xs;
  flex-shrink: 0;
}

.week-appointment-card-time span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.week-appointment-card-patient {
  font-size: 10px;
  font-weight: 500;
  margin-top: calc(#{$layout-spacing-sm} / 4);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
}

.popover-container {
  display: none;
  position: absolute;
  z-index: 1000;
  min-width: 250px;
  max-width: 300px;
  pointer-events: none;
}

.popover-container .card {
  margin: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  pointer-events: auto;
}

.popover-container .card-body {
  padding: $layout-spacing;
}

.popover-content-item {
  display: flex;
  flex-direction: column;
  margin-bottom: 0.75rem;

  &:last-child {
    margin-bottom: 0;
  }

  strong {
    font-size: $font-size-xs;
    color: $gray-color-dark;
    margin-bottom: 0.25rem;
    font-weight: 600;
  }

  span {
    font-size: $font-size-sm;
    color: $dark-color;
    word-wrap: break-word;
    line-height: 1.4;
  }
}

</style>
